import * as XLSX from 'xlsx';
import type { ColumnMeta, Row } from '@/lib/biData';

// ============================================================
// Zifek BI — exports (CSV, Excel, PDF, image de graphique).
// ============================================================

function cellValue(v: string | number | boolean | null | undefined): string | number {
  if (v === null || v === undefined) return '';
  if (typeof v === 'number' || typeof v === 'boolean') return v as number | string;
  return String(v);
}

/** Déclenche le téléchargement d'un Blob. */
function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function safeName(name: string): string {
  return (name || 'export').replace(/[^\w.-]+/g, '_').slice(0, 80);
}

/** Exporte les lignes en CSV (séparateur point-virgule pour Excel FR). */
export function exportBiCsv(name: string, columns: ColumnMeta[], rows: Row[]): void {
  const header = columns.map((c) => c.name).join(';');
  const lines = rows.map((r) =>
    columns
      .map((c) => {
        const raw = r[c.name];
        const s = raw === null || raw === undefined ? '' : String(raw);
        return s.includes(';') || s.includes('\n') || s.includes('"')
          ? `"${s.replace(/"/g, '""')}"`
          : s;
      })
      .join(';'),
  );
  const csv = '\uFEFF' + [header, ...lines].join('\n');
  download(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${safeName(name)}.csv`);
}

/** Exporte les lignes en classeur Excel (.xlsx). */
export function exportBiExcel(name: string, columns: ColumnMeta[], rows: Row[]): void {
  const aoa: (string | number | boolean)[][] = [];
  aoa.push(columns.map((c) => c.name));
  rows.forEach((r) => aoa.push(columns.map((c) => cellValue(r[c.name]))));
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Données');
  const out = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  download(new Blob([out], { type: 'application/octet-stream' }), `${safeName(name)}.xlsx`);
}

function esc(s: unknown): string {
  return String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * Exporte un rapport PDF via la boîte d'impression du navigateur
 * (l'utilisateur choisit « Enregistrer au format PDF »).
 */
export function exportBiPdf(
  name: string,
  columns: ColumnMeta[],
  rows: Row[],
  extras: { kpis?: { label: string; value: string }[]; charts?: { title: string; canvas?: string }[] } = {},
): void {
  const kpiHtml = (extras.kpis || [])
    .map((k) => `<div class="kpi"><span>${esc(k.label)}</span><strong>${esc(k.value)}</strong></div>`)
    .join('');
  const chartsHtml = (extras.charts || [])
    .map((c) => (c.canvas ? `<div class="chart"><h3>${esc(c.title)}</h3><img src="${c.canvas}" /></div>` : ''))
    .join('');
  const thead = `<tr>${columns.map((c) => `<th>${esc(c.name)}</th>`).join('')}</tr>`;
  const tbody = rows
    .slice(0, 500)
    .map((r) => `<tr>${columns.map((c) => `<td>${esc(r[c.name])}</td>`).join('')}</tr>`)
    .join('');

  const html = `<!DOCTYPE html><html lang="fr"><head><meta charset="utf-8"><title>${esc(name)}</title>
  <style>
    *{box-sizing:border-box}body{font-family:Inter,Arial,sans-serif;color:#1a1817;margin:32px}
    h1{font-size:20px;margin:0 0 4px}h2{font-size:13px;color:#666;font-weight:400;margin:0 0 20px}
    .kpis{display:flex;flex-wrap:wrap;gap:12px;margin-bottom:20px}
    .kpi{border:1px solid #ddd;border-radius:10px;padding:10px 14px;min-width:130px}
    .kpi span{display:block;font-size:11px;color:#777}.kpi strong{font-size:18px}
    .chart{page-break-inside:avoid;margin:16px 0}.chart img{max-width:640px;border:1px solid #eee;border-radius:8px}
    table{width:100%;border-collapse:collapse;font-size:11px;margin-top:12px}
    th,td{border:1px solid #e2ddd6;padding:5px 7px;text-align:left}
    th{background:#f5f1ec}
  </style></head><body>
    <h1>${esc(name)}</h1><h2>Rapport généré par Zifek BI — ${new Date().toLocaleDateString('fr-FR')} · ${rows.length} lignes</h2>
    ${kpiHtml ? `<div class="kpis">${kpiHtml}</div>` : ''}
    ${chartsHtml}
    <table><thead>${thead}</thead><tbody>${tbody}</tbody></table>
  </body></html>`;

  const w = window.open('', '_blank');
  if (!w) return;
  w.document.write(html);
  w.document.close();
  setTimeout(() => w.print(), 400);
}

/** Exporte un graphique en image PNG (SVG Recharts → canvas → PNG). */
export async function exportChartImage(title: string, svg: SVGSVGElement | null): Promise<void> {
  if (!svg) return;
  const clone = svg.cloneNode(true) as SVGSVGElement;
  clone.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
  const bounds = svg.getBoundingClientRect();
  clone.setAttribute('width', String(Math.max(bounds.width, 400)));
  clone.setAttribute('height', String(Math.max(bounds.height, 240)));
  const xml = new XMLSerializer().serializeToString(clone);
  const svg64 = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(xml)}`;

  await new Promise<void>((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width || 800;
      canvas.height = img.height || 480;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
          if (blob) download(blob, `${safeName(title)}.png`);
          resolve();
        }, 'image/png');
      } else {
        resolve();
      }
    };
    img.onerror = () => resolve();
    img.src = svg64;
  });
}